import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { map } from 'rxjs';

import { AuthService } from '../services/auth/auth.service';

/**
 * Protects authenticated routes.
 * Calls /api/auth/current to verify the session cookie is still valid.
 * Redirects to /login when not authenticated.
 *
 * When the user's company requires MFA but the user hasn't set it up yet,
 * all routes except /security redirect to /security so they're forced to
 * configure MFA before using the rest of the app.
 *
 * NOTE: This guard is placed on the parent shell route (path: ''), so
 * route.url is always an empty array. We use state.url (the full destination
 * URL from RouterStateSnapshot) to determine the actual target path.
 */
export const authGuard: CanActivateFn = (
  _route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const auth   = inject(AuthService);
  const router = inject(Router);

  return auth.isLoggedIn().pipe(
    map(loggedIn => {
      if (!loggedIn) {
        return router.createUrlTree(['/login']);
      }

      // Enforce MFA setup when the company requires it.
      // Use state.url (full destination path) — route.url is always empty
      // because the guard lives on the parent shell route (path: '').
      const user = auth.user();
      if (user?.mfa_setup_required) {
        const destPath = state.url.split('?')[0]; // strip query params
        if (!destPath.startsWith('/security')) {
          return router.createUrlTree(['/security'], {
            queryParams: { mfa_required: '1' },
          });
        }
      }

      return true;
    })
  );
};
